// ── ClientPortalPage ──
// Provision and manage borrower client-portal logins (dbo.customerUser): create, list, edit, delete, reset password.
import { useState, useEffect } from "react";
import { B } from "../../../shared/theme";
import { Btn, Card, ErrorMsg, FInput, FSelect, Modal, SearchBar, SectionHeader, Spinner, STh, Td, Th } from "../../../shared/ui";
import { useSortable } from "../../../shared/hooks";
import { localFilter } from "../../../shared/format";
import { useApp } from "../../../shared/appContext";
import { ContactBadge, contactState } from "../../../shared/portalAccess";

const lbl = { fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 };
const badge = (bg, fg, label) => <span style={{ fontSize: 12, background: bg, color: fg, padding: "2px 8px", borderRadius: 4, fontWeight: 600, whiteSpace: "nowrap" }}>{label}</span>;
// ── Generated password for a client portal login ────────────────────────────
//
// This used to use Math.random(). Math.random() is not a cryptographic
// generator: its output comes from a seeded internal state, and an observer who
// has seen enough of that stream can recover the state and predict what comes
// next. Fine for shuffling a list, wrong for a credential — especially this one,
// which is issued to an outside party and never expires on its own.
//
// crypto.getRandomValues is the browser's CSPRNG and is a drop-in here. It has
// been available in every browser this app supports for years, and it throws
// rather than silently degrading, so a failure would be loud rather than quiet.
//
// On the modulo: the alphabet is exactly 64 characters and 256 divides evenly
// by 64, so `byte % 64` maps every byte onto exactly four characters and adds
// no bias. That stops being true if the alphabet length changes — keep it a
// power of two, or switch to rejection sampling.
//
// Alphabet, length and approach deliberately mirror the server-side generator
// used for staff accounts, minus the glyphs that get misread when read aloud.
const PW_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%*?";
const PW_LENGTH = 16;

const genPw = () => {
  const bytes = new Uint8Array(PW_LENGTH);
  crypto.getRandomValues(bytes);
  let o = "";
  for (let i = 0; i < PW_LENGTH; i++) o += PW_ALPHABET[bytes[i] % PW_ALPHABET.length];
  return o;
};

export const ClientPortalPage = () => {
  const { api, audit, setConfirm, delRec } = useApp();
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState("");

  // create form
  const [client, setClient] = useState({ email: "", firstName: "", lastName: "", accountId: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [createErr, setCreateErr] = useState(null);
  const [createResult, setCreateResult] = useState(null);   // { email, password }

  // edit / reset
  const [editUser, setEditUser] = useState(null);   // { id, email, firstName, lastName, accountId, status }
  const [resetUser, setResetUser] = useState(null); // { user, done?, password? }
  const [resetPw, setResetPw] = useState("");
  const [saving, setSaving] = useState(false);

  const acctOptions = [{ value: "", label: "— Select account —" }, ...accounts.map(a => ({ value: String(a.id), label: a.companyname + (a.accountnumber ? ` (${a.accountnumber})` : "") }))];
  const acctName = id => { const a = accounts.find(x => String(x.id) === String(id)); return a ? a.companyname : (id || "—"); };

  const load = () => {
    setLoading(true);
    api.get("getCustomerUsers")
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    api.get("getAccounts")
      .then(d => setAccounts((Array.isArray(d) ? d : []).slice().sort((a, b) => (a.companyname || "").localeCompare(b.companyname || ""))))
      .catch(() => setAccounts([]));
  }, []);

  const saveCreate = async () => {
    setBusy(true); setCreateErr(null); setCreateResult(null);
    try {
      await api.post("createCustomerUser", { email: client.email, firstName: client.firstName, lastName: client.lastName, accountId: client.accountId, password: client.password });
      try { await audit("Created client user", "customerUser", client.email, "Created client portal login " + client.email); } catch (e) {}
      setCreateResult({ email: client.email, password: client.password });
      setClient({ email: "", firstName: "", lastName: "", accountId: "", password: "" });
      load();
    } catch (e) { setCreateErr(e.message); }
    finally { setBusy(false); }
  };

  const openEdit = u => setEditUser({ id: u.id, email: u.email, origEmail: u.email, firstName: u.firstName || "", lastName: u.lastName || "", accountId: u.accountId != null ? String(u.accountId) : "", status: u.status || "active" });
  const saveEdit = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.post("updateCustomerUser", { id: editUser.id, email: editUser.email, firstName: editUser.firstName, lastName: editUser.lastName, accountId: editUser.accountId, status: editUser.status });
      const detail = r && r.emailChanged
        ? `Updated client portal login; email ${r.oldEmail} → ${r.email} (trusted browsers revoked)`
        : "Updated client portal login " + editUser.email;
      try { await audit("Edited client user", "customerUser", (r && r.email) || editUser.email, detail); } catch (e) {}
      setEditUser(null); load();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // Delete via the app's generic deleteRecord (customerUser was added to it server-side).
  const askDelete = u => setConfirm({
    msg: `Delete the client portal login for ${u.email}? They will no longer be able to sign in. This cannot be undone.`,
    onConfirm: () => delRec("customerUser", u.id, "Deleted client portal login " + u.email, load),
  });

  const openReset = u => { setResetPw(""); setResetUser({ user: u }); };
  const doReset = async () => {
    setSaving(true); setErr(null);
    try {
      await api.post("resetCustomerUserPassword", { id: resetUser.user.id, password: resetPw });
      try { await audit("Reset client password", "customerUser", resetUser.user.email, "Reset password for " + resetUser.user.email); } catch (e) {}
      setResetUser({ user: resetUser.user, done: true, password: resetPw });
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  // Decorated before filtering so the borrower's name is searchable too — it is
  // resolved from accountId for display, and somebody looking for "who has a
  // login at Hobbs" would otherwise have to know the person's email first.
  const rows = users.map(u => ({
    ...u,
    companyname: acctName(u.accountId),
    fullname: [u.firstName, u.lastName].filter(Boolean).join(" "),
    // Sort keys: what each column actually shows, so a sort reads the way the
    // column looks. Blank → null so empties sort last rather than first.
    namesort:    [u.firstName, u.lastName].filter(Boolean).join(" ") || null,
    statussort:  (u.status || "active") === "active" ? "Active" : "Inactive",
    contactsort: (s => (s.key === "linked" && s.note ? s.note : s.label))(contactState(u)),
  }));
  const filteredRows = localFilter(rows, q, ["email", "firstName", "lastName", "fullname", "companyname", "contactname"]);
  const { sorted: shown, sortKey, sortDir, toggle } = useSortable(filteredRows, "email", "asc");
  const SortTh = ({ label, k }) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} />;

  // The mirror of the Contacts page worklist. There the gap is "flagged but no
  // login"; here it is "signs in but nobody holds them in Contacts". Counted
  // off the FULL list, not the filtered one — a total that shrinks when you
  // type in the search box is not a total.
  const orphans = rows.filter(u => contactState(u).key === "none");
  const misfiled = rows.filter(u => contactState(u).key === "otheraccount");

  if (loading) return <Spinner />;
  if (err && !users.length) return <ErrorMsg msg={err} onRetry={load} />;

  return <>
    <SectionHeader title="Client Portal Login" />

    {err && <div style={{ color: "#993C1D", fontSize: 12, marginBottom: 10 }}>{err}</div>}

    {/* ── Create ── */}
    <Card style={{ marginBottom: 16, padding: 16 }}>
      <div style={{ ...lbl, marginBottom: 10, fontSize: 12 }}>Add client portal login</div>
      <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 14 }}>
        Provision a borrower's client-portal login. You set the password here (min 8 characters) — it's hashed on the server and stored in the client user table. Share the password securely; the client should change it after first sign-in.
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
        <div><div style={lbl}>Email</div><FInput value={client.email} onChange={v => setClient({ ...client, email: v })} /></div>
        <div><div style={lbl}>Account (borrower)</div>
          <FSelect value={client.accountId} onChange={v => setClient({ ...client, accountId: v })} options={acctOptions} /></div>
        <div><div style={lbl}>First Name</div><FInput value={client.firstName} onChange={v => setClient({ ...client, firstName: v })} /></div>
        <div><div style={lbl}>Last Name</div><FInput value={client.lastName} onChange={v => setClient({ ...client, lastName: v })} /></div>
        <div style={{ gridColumn: "1 / -1" }}>
          <div style={lbl}>Password <span style={{ textTransform: "none", fontWeight: 400 }}>(min 8 characters)</span></div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}><FInput value={client.password} onChange={v => setClient({ ...client, password: v })} /></div>
            <Btn onClick={() => setClient({ ...client, password: genPw() })}>Generate</Btn>
          </div>
        </div>
      </div>
      {createErr && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 12 }}>{createErr}</div>}
      {createResult && (
        <div style={{ marginTop: 14, padding: "10px 12px", borderRadius: 6, background: "#E1F5EE", border: "1px solid #0F6E56", fontSize: 13, color: "#0F6E56" }}>
          Client login created for <strong>{createResult.email}</strong>. Password: <code style={{ fontFamily: "monospace", background: "#fff", padding: "1px 6px", borderRadius: 4 }}>{createResult.password}</code> — copy it now and share it securely.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
        <Btn variant="primary" disabled={busy || !client.email || !client.accountId || client.password.length < 8} onClick={saveCreate}>{busy ? "Creating…" : "Create Client User"}</Btn>
      </div>
    </Card>

    {/* ── List ── */}
    <Card style={{ marginBottom: 24, overflowX: "auto" }}>
      {orphans.length > 0 && (
        <div style={{ fontSize: 12, background: "#FBE4DD", color: "#993C1D", border: "1px solid #F0C4B6",
                      borderRadius: 6, padding: "8px 12px", marginBottom: 12, lineHeight: 1.5 }}>
          <b>{orphans.length}</b> {orphans.length === 1 ? "login has" : "logins have"} no contact record on
          that borrower. {orphans.length === 1 ? "That person" : "Those people"} can sign in and read
          statements, but {orphans.length === 1 ? "does" : "do"} not appear in Contacts — so
          {orphans.length === 1 ? " they are" : " they are"} invisible to the invoice notices and to
          anyone looking the borrower up.
        </div>
      )}
      {misfiled.length > 0 && (
        <div style={{ fontSize: 12, background: "#FDF0D5", color: "#7A5200", border: "1px solid #EBD9A8",
                      borderRadius: 6, padding: "8px 12px", marginBottom: 12, lineHeight: 1.5 }}>
          <b>{misfiled.length}</b> {misfiled.length === 1 ? "login is" : "logins are"} on one borrower
          while the matching contact sits on another. Not missing — misfiled. Move whichever of the two
          is on the wrong account rather than creating a second copy of the person.
        </div>
      )}

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <SearchBar value={q} onChange={setQ} placeholder="Email, name, borrower, or contact…" />
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
          {q
            ? `${shown.length} of ${users.length} login${users.length === 1 ? "" : "s"}`
            : `${users.length} login${users.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          <SortTh label="Email" k="email" />
          <SortTh label="Name" k="namesort" />
          <SortTh label="Account" k="companyname" />
          <SortTh label="Status" k="statussort" />
          <SortTh label="Contact" k="contactsort" />
          <Th></Th>
        </tr></thead>
        <tbody>
          {/* Two different empty states, deliberately. "Nothing matches" and
              "nothing exists" look identical in a table and mean opposite
              things — one is a search to clear, the other is work to do. */}
          {shown.length === 0
            ? <tr><td colSpan={6} style={{ padding: 20, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
                {users.length === 0
                  ? "No client portal logins yet."
                  : <>No login matches <strong>{q}</strong>. <span style={{ color: B.cyan, cursor: "pointer", textDecoration: "underline" }}
                       onClick={() => setQ("")}>Clear the search</span> to see all {users.length}.</>}
              </td></tr>
            : shown.map(u => (
              <tr key={u.id}
                onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"}
                onMouseLeave={e => e.currentTarget.style.background = ""}>
                <Td style={{ fontWeight: 500 }}>{u.email || "—"}</Td>
                <Td>{[u.firstName, u.lastName].filter(Boolean).join(" ") || "—"}</Td>
                <Td>{acctName(u.accountId)}</Td>
                <Td>{(u.status || "active") === "active" ? badge("#E1F5EE", "#0F6E56", "Active") : badge("#F1EFE8", "#888780", "Inactive")}</Td>
                <Td><ContactBadge user={u} /></Td>
                <Td><div style={{ display: "flex", gap: 6 }}>
                  <Btn onClick={() => openEdit(u)}>Edit</Btn>
                  <Btn onClick={() => openReset(u)}>Reset Password</Btn>
                  <Btn variant="danger" onClick={() => askDelete(u)}>Delete</Btn>
                </div></Td>
              </tr>
            ))}
        </tbody>
      </table>
    </Card>

    {/* Edit */}
    {editUser && (
      <Modal title={"Edit — " + editUser.origEmail} onClose={() => setEditUser(null)} wide>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 16px" }}>
          <div><div style={lbl}>Email</div><FInput value={editUser.email} onChange={v => setEditUser({ ...editUser, email: v })} />
            {String(editUser.email || "").trim().toLowerCase() !== String(editUser.origEmail || "").trim().toLowerCase() && (
              <div style={{ fontSize: 11, color: "#7A5200", marginTop: 4 }}>
                The borrower will sign in with the new email, and sign-in codes go there. Their trusted browsers are reset; the password is unchanged.
              </div>)}</div>
          <div><div style={lbl}>Account (borrower)</div>
            <FSelect value={editUser.accountId} onChange={v => setEditUser({ ...editUser, accountId: v })} options={acctOptions} /></div>
          <div><div style={lbl}>First Name</div><FInput value={editUser.firstName} onChange={v => setEditUser({ ...editUser, firstName: v })} /></div>
          <div><div style={lbl}>Last Name</div><FInput value={editUser.lastName} onChange={v => setEditUser({ ...editUser, lastName: v })} /></div>
          <div><div style={lbl}>Status</div>
            <FSelect value={editUser.status} onChange={v => setEditUser({ ...editUser, status: v })}
              options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} /></div>
        </div>
        {err && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 12 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <Btn onClick={() => setEditUser(null)}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={saveEdit}>{saving ? "Saving…" : "Save"}</Btn>
        </div>
      </Modal>
    )}

    {/* Reset password */}
    {resetUser && (
      <Modal title={"Reset password — " + resetUser.user.email} onClose={() => setResetUser(null)}>
        {resetUser.done
          ? <>
            <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 12 }}>
              Password reset for <strong>{resetUser.user.email}</strong>. New password: <code style={{ fontFamily: "monospace", background: "#EAF7FB", padding: "2px 8px", borderRadius: 4, fontSize: 14 }}>{resetUser.password}</code> — copy it now and share it securely.
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}><Btn onClick={() => setResetUser(null)}>Done</Btn></div>
          </>
          : <>
            <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginBottom: 12 }}>Set a new password (min 8 characters). The client's current password stops working immediately.</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              <div style={{ flex: 1 }}><FInput value={resetPw} onChange={setResetPw} /></div>
              <Btn onClick={() => setResetPw(genPw())}>Generate</Btn>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn onClick={() => setResetUser(null)}>Cancel</Btn>
              <Btn variant="primary" disabled={saving || resetPw.length < 8} onClick={doReset}>{saving ? "Resetting…" : "Reset Password"}</Btn>
            </div>
          </>}
      </Modal>
    )}
  </>;
};
